// src/pages/WelcomePage.jsx
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LocationContext } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { CartContext } from '../contexts/CartContext';
import GoogleMapsService from '../services/GoogleMapsService';
import '../styles/welcome-page.css';

import HamburgerButton from '../components/ui/HamburgerButton';
import HamburgerMenu from '../components/ui/HamburgerMenu';

// Import des images de promotion
import promo1 from '../assets/images/promos/promo1.jpg';
import promo2 from '../assets/images/promos/promo2.jpg';
import promo3 from '../assets/images/promos/promo3.jpg';
import logoImage from '../assets/logo.png';

// Import des images de catégories
import waterIcon from '../assets/images/categories/water-category.png';
import sodaIcon from '../assets/images/categories/soda-category.png';
import beerIcon from '../assets/images/categories/beer-category.png';
import draftBeerIcon from '../assets/images/categories/draft-beer-category.png';
import wineIcon from '../assets/images/categories/wine-category.png';
import spiritsIcon from '../assets/images/categories/spirits-category.png';

const WelcomePage = () => {
  const navigate = useNavigate();
  const { 
    deliveryAddress, 
    setDeliveryLocationData, 
    deliveryHistory, 
    selectFromDeliveryHistory, 
    removeFromDeliveryHistory,
    setDistributorChangeCallback
  } = useContext(LocationContext);
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { clearCart } = useContext(CartContext);
  
  const [currentSlide, setCurrentSlide] = useState(0);
  const [locationInput, setLocationInput] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [isSelectingLocation, setIsSelectingLocation] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [isLoadingPredictions, setIsLoadingPredictions] = useState(false);
  const [showRecentLocations, setShowRecentLocations] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false); // Nouveau état pour suivre l'initialisation
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  const inputRef = useRef(null);
  const predictionsRef = useRef(null);
  
  // Fonction pour obtenir la salutation selon l'heure
  const getGreeting = () => {
    const hour = new Date().getHours();
    
    if (hour >= 1 && hour < 18) {
      return 'Bonjour';
    } else {
      return 'Bonsoir';
    }
  };
  
  // Gestion du menu hamburger
  const toggleMenu = () => {
	setIsMenuOpen(!isMenuOpen);
  };
  
  const closeMenu = () => {
    setIsMenuOpen(false);
  };
  
  // Images du carrousel
  const promoImages = [promo1, promo2, promo3];
  
  // Catégories de boissons avec les routes corrigées
  const categories = [
    { id: 'water', name: 'Eaux', icon: waterIcon, color: '#e8f5e9', route: '/products/eaux', disabled: false },
    { id: 'soda', name: 'Boissons Gazeuses', icon: sodaIcon, color: '#ffebee', route: '/products/sodas', disabled: false },
    { id: 'beers', name: 'Bières et Alcools-Mix', icon: beerIcon, color: '#fff8e1', route: '/products/bieres', disabled: false }, // MODIFIÉ : Activé et route changée
    { id: 'draft-beer', name: 'Bières à Pression', icon: draftBeerIcon, color: '#fff3e0', route: '/beer', disabled: true },
    { id: 'wine', name: 'Vins', icon: wineIcon, color: '#f3e5f5', route: '/wine', disabled: true },
    { id: 'spirits', name: 'Spiritueux', icon: spiritsIcon, color: '#e8eaf6', route: '/spirits', disabled: true }
  ];
  
  // Configuration du callback pour vider le panier
  useEffect(() => {
    // Définir le callback pour vider le panier lors du changement de distributeur
    setDistributorChangeCallback(() => (oldId, newId) => {
      console.log(`Distributeur changé de ${oldId} vers ${newId}`);
      clearCart();
      
      // Stocker l'information pour l'afficher sur la page des produits
      sessionStorage.setItem('cartCleared', 'true');
    });
    
    // Cleanup
    return () => {
      setDistributorChangeCallback(null);
    };
  }, [setDistributorChangeCallback, clearCart]);
  
  // Initialiser la page avec le lieu de livraison existant
  useEffect(() => {
    // Ne pas réinitialiser si déjà initialisé
    if (isInitialized) return;
    
    // Vérifier si une adresse de livraison existe déjà 
    if (deliveryAddress) {
      setLocationInput(deliveryAddress);
      setShowCategories(true);
      setShowRecentLocations(false);
      console.log("📍 Lieu de livraison existant chargé:", deliveryAddress);
    } else {
      setLocationInput('');
      setShowCategories(false);
      setShowRecentLocations(true);
      console.log("🔄 Aucun lieu de livraison enregistré");
    }
    
    setPredictions([]);
    setShowPredictions(false);
    setIsInitialized(true);
  }, [deliveryAddress, isInitialized]);
  
  // Vérifier l'authentification et rediriger si nécessaire
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      // Rediriger vers la page de connexion avec l'URL de retour
      navigate('/register', { 
        state: { from: { pathname: '/welcome-page' } },
        replace: true 
      });
    }
  }, [isAuthenticated, authLoading, navigate]);
  
  // Initialiser Google Maps au chargement du composant
  useEffect(() => {
    GoogleMapsService.loadGoogleMaps().catch(error => {
      console.error('Erreur lors du chargement de Google Maps:', error);
    });
  }, []);
  
  // Effet pour le défilement automatique du carrousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % promoImages.length);
    }, 4000); // Change toutes les 4 secondes
    
    return () => clearInterval(timer);
  }, [promoImages.length]);
  
  // Gérer les clics en dehors des prédictions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (predictionsRef.current && !predictionsRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowPredictions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Gestionnaire pour les points du carrousel
  const handleDotClick = (index) => {
    setCurrentSlide(index);
  };
  
  // Gestionnaire pour la saisie dans le champ de localisation
  const handleLocationInputChange = async (e) => {
    const value = e.target.value;
    setLocationInput(value);
    
    // Masquer les lieux récents quand l'utilisateur commence à taper
    if (value.length > 0) {
      setShowRecentLocations(false);
    } else {
      setShowRecentLocations(true);
    }
    
    if (value.length > 2) {
      setIsLoadingPredictions(true);
      try {
        const results = await GoogleMapsService.getPlacePredictions(value);
        console.log('Nombre de prédictions reçues:', results.length);
        setPredictions(results);
        setShowPredictions(true);
      } catch (error) {
        console.error('Erreur lors de la recherche:', error);
        setPredictions([]);
      } finally {
        setIsLoadingPredictions(false);
      }
    } else {
      setPredictions([]);
      setShowPredictions(false);
    }
  };
  
  // Gestionnaire pour la sélection d'une prédiction
  const handlePredictionSelect = async (prediction) => {
    setIsSelectingLocation(true);
    setShowPredictions(false);
    
    try {
      // Mettre à jour l'input avec l'adresse sélectionnée
      setLocationInput(prediction.description);
      
      // Récupérer les détails du lieu
      const placeDetails = await GoogleMapsService.getPlaceDetails(prediction.place_id);
      
      // Enregistrer le lieu de livraison
      await setDeliveryLocationData(placeDetails, prediction.description);
      
      // Afficher les catégories avec une petite animation
      setTimeout(() => {
        setShowCategories(true);
      }, 300);
      
      console.log('Lieu de livraison enregistré:', placeDetails);
      
    } catch (error) {
      console.error('Erreur lors de la sélection du lieu:', error);
      alert('Une erreur est survenue lors de la sélection du lieu. Veuillez réessayer.');
    } finally {
      setIsSelectingLocation(false);
    }
  };
  
  // Gestionnaire pour la sélection d'un lieu récent
  const handleRecentLocationSelect = async (location) => {
    setIsSelectingLocation(true);
    setShowRecentLocations(false);
    
    try {
      setLocationInput(location.address);
      
      await selectFromDeliveryHistory(location);
      
      setTimeout(() => {
        setShowCategories(true);
      }, 300);
      
      console.log('Lieu récent sélectionné:', location);
      
    } catch (error) {
      console.error('Erreur lors de la sélection du lieu récent:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSelectingLocation(false);
    }
  };
  
  // Gestionnaire pour supprimer un lieu récent
  const handleRemoveRecentLocation = (e, locationId) => {
    e.stopPropagation(); // Empêcher la sélection du lieu
    removeFromDeliveryHistory(locationId);
  };
  
  // Gestionnaire pour le clic sur une catégorie
  const handleCategoryClick = (category) => {
    // Ne naviguer que si la catégorie est active ET que les catégories sont affichées
    if (showCategories && !category.disabled) {
      navigate(category.route);
    }
    // Si la catégorie est désactivée, on pourrait afficher un message
    else if (category.disabled) {
      // Optionnel : vous pouvez ajouter une notification ou un effet visuel
      console.log('Cette catégorie sera bientôt disponible');
    }
  };
  
  // Gestionnaire pour le focus sur l'input
  const handleInputFocus = () => {
    if (predictions.length > 0) {
      setShowPredictions(true);
    } else if (locationInput.length === 0) {
      setShowRecentLocations(true);
    }
  };
  
  // Si l'authentification est en cours de vérification, afficher un loader
  if (authLoading) {
    return (
      <div className="welcome-page">
        <div className="loading-container">
          <span className="loading-spinner"></span>
          <span>Chargement...</span>
        </div>
      </div>
    );
  }
  
  // Si non authentifié, la redirection aura déjà eu lieu via useEffect
  if (!isAuthenticated || !user) {
    return null;
  }
  
  return (
    <div className="welcome-page">
      {/* Menu hamburger */}
      <HamburgerMenu isOpen={isMenuOpen} onClose={closeMenu} />
      
      {/* ===== En-tête sticky : bande rouge + logo + bande fine ===== */}
      <div className="welcome-sticky-header">
        {/* Bande rouge supérieure */}
        <div className="top-red-band"></div>

        {/* Logo avec hamburger */}
        <div className="welcome-header">
          <img src={logoImage} alt="Boissons du Cameroun" className="location-logo" />

          {/* ✨ Bouton hamburger */}
          <div className="welcome-hamburger-button">
            <HamburgerButton isOpen={isMenuOpen} onClick={toggleMenu} />
          </div>
        </div>

        {/* Bande rouge fine */}
        <div className="thin-red-band"></div>
      </div>

      {/* ===== Contenu scrollable ===== */}
      <div className="welcome-content-wrapper">

        {/* Bannière de livraison 24h - Toujours visible en haut */}
      <div className="delivery-banner-24h-top">
        <div className="banner-content">
          <div className="banner-icon-wrapper">
            <svg className="delivery-truck-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.01 17H7V6H19V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 17H16.01M6 17H7M19 17H20V13L23 11V17H19.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="7.5" cy="17.5" r="2.5" stroke="currentColor" strokeWidth="2"/>
              <circle cx="16.5" cy="17.5" r="2.5" stroke="currentColor" strokeWidth="2"/>
              <path d="M2 17H4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M2 14L2 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <div className="speed-lines">
              <span className="speed-line speed-line-1"></span>
              <span className="speed-line speed-line-2"></span>
              <span className="speed-line speed-line-3"></span>
            </div>
          </div>
          <div className="banner-text">
            <h3 className="banner-title">
			  <span className="greeting-name">{getGreeting()} {user.prenom || 'Cher(e) client(e)'} !</span>
              <span className="delivery-promise">
                <span className="title-highlight">Faites vous livrer</span>
                <span className="time-highlight">en 24h Chez Vous !</span>
              </span>
            </h3>
            <p className="banner-subtitle">Service de livraison rapide à domicile</p>
          </div>
          <div className="banner-badge">
            <span className="badge-text">EXPRESS</span>
          </div>
        </div>
      </div>
      
      {/* Carrousel promotionnel */}
      <div className="promo-carousel">
        <div className="carousel-wrapper">
          <div 
            className="carousel-inner"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {promoImages.map((image, index) => (
              <div key={index} className="carousel-slide">
                <img src={image} alt={`Promotion ${index + 1}`} />
              </div>
            ))}
          </div>
        </div>
        
        {/* Indicateurs du carrousel */}
        <div className="carousel-dots">
          {promoImages.map((_, index) => (
            <button
              key={index}
              className={`dot ${currentSlide === index ? 'active' : ''}`}
              onClick={() => handleDotClick(index)}
              aria-label={`Aller à la slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
      
      {/* Section de livraison */}
      <div className="delivery-section">
		{!showCategories && <h2 className="section-title">Où souhaitez-vous être livré ?</h2>}
        <div className="location-input-wrapper">
          <input
            ref={inputRef}
            type="text"
            className="location-input"
            placeholder="Entrez votre lieu de livraison"
            value={locationInput}
            onChange={handleLocationInputChange}
            onFocus={handleInputFocus}
            disabled={isSelectingLocation}
          />
          <svg className="location-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          
          {/* Liste des prédictions */}
          {showPredictions && predictions.length > 0 && (
            <div ref={predictionsRef} className="predictions-dropdown">
              {isLoadingPredictions ? (
                <div className="prediction-item loading">
                  <span className="loading-text">Recherche en cours...</span>
                </div>
              ) : (
                predictions.map((prediction) => (
                  <div
                    key={prediction.place_id}
                    className="prediction-item"
                    onClick={() => handlePredictionSelect(prediction)}
                  >
                    <svg className="prediction-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      {prediction.resultType === 'establishment' ? (
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                      ) : (
                        <>
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                          <circle cx="12" cy="10" r="3" />
                        </>
                      )}
                    </svg>
                    
                    <div className="prediction-text">
                      <span className="prediction-main">{prediction.structured_formatting.main_text}</span>
                      <span className="prediction-secondary">
                        {prediction.resultType === 'establishment' ? 'établissement' : 'adresse'} • {prediction.structured_formatting.secondary_text}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          
          {/* Liste des lieux récents */}
          {showRecentLocations && deliveryHistory.length > 0 && !showCategories && locationInput.length === 0 && (
            <div className="recent-locations-dropdown">
              <div className="recent-locations-header">
                <span className="recent-locations-title">Lieux récents</span>
              </div>
              {deliveryHistory.map((location) => (
                <div
                  key={location.id}
                  className="recent-location-item"
                  onClick={() => handleRecentLocationSelect(location)}
                >
                  <svg className="recent-location-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                    <circle cx="12" cy="9" r="2.5" />
                  </svg>
                  
                  <div className="recent-location-text">
                    <span className="recent-location-name">{location.name}</span>
                    <span className="recent-location-address">{location.address}</span>
                    {location.useCount > 1 && (
                      <span className="recent-location-count">Utilisé {location.useCount} fois</span>
                    )}
                  </div>
                  
                  <button
                    className="recent-location-remove"
                    onClick={(e) => handleRemoveRecentLocation(e, location.id)}
                    aria-label="Supprimer ce lieu"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* Indicateur de chargement lors de la sélection */}
          {isSelectingLocation && (
            <div className="location-loading">
              <span className="loading-spinner"></span>
              <span>Enregistrement du lieu...</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Section des catégories - Affichée uniquement si un lieu est sélectionné */}
      {showCategories && (
        <div className="categories-section fade-in">
          <h2 className="section-title">Faites vos achats</h2>
          <div className="categories-grid">
            {categories.map((category) => (
              <div 
                key={category.id} 
                className={`category-box ${category.disabled ? 'category-disabled' : ''}`}
                onClick={() => handleCategoryClick(category)}
                style={{ 
                  backgroundImage: `url(${category.icon})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  cursor: category.disabled ? 'not-allowed' : 'pointer'
                }}
              >
                <div className="category-overlay">
                  <span className="category-name">{category.name}</span>
                  {category.disabled && (
                    <span className="category-unavailable">Bientôt disponible</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Message si aucun lieu n'est sélectionné */}
      {!showCategories && !isSelectingLocation && (
        <div className="no-location-message">
          <p className="location-message-title">Choisissez votre adresse de livraison et composez votre commande !</p>
        </div>
      )}
      
      </div> {/* fin .welcome-content-wrapper */}

      {/* Footer */}
      <footer className="welcome-footer">
        <p className="footer-text">
          {/* Visitez notre site web : {' '} */}
          <a href="https://www.boissonsducameroun.com" target="_blank" rel="noopener noreferrer">
            www.boissonsducameroun.com
          </a>
        </p>
      </footer>
    </div>
  );
};

export default WelcomePage;